

//  end 

import React, { useState, useEffect, useRef } from "react";
import "./Translator.css";
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Edit } from 'lucide-react';
import { CircularProgress } from '@mui/material';

const API_BASE = "https://api.waraq.co/waraq/api/v1";

const Translator = () => {

  const [data, setData] = useState([]);
  const [openRequests, setOpenRequests] = useState({});
  const [currentOpenRequestId, setCurrentOpenRequestId] = useState(null);
  const [requestID, setRequestID] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [uploadingRows, setUploadingRows] = useState({});
  const [showPopup, setShowPopup] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [successfulDelete , setSuccessfulDelete]= useState(null)

  const [errorMessage, setErrorMessage] = useState(null);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [successfulMessage, setSuccessfulMessage] = useState(null);

  const fileInputRefs = useRef({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem("jwtToken");
    return { Authorization: `${token}` };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const showMessage = (setter, message, duration = 3000) => {
    setter(message);
    setTimeout(() => setter(null), duration);
  };

  const getTranslateData = async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/user/translate/requests/translating`,
        { headers: getAuthHeaders() }
      );
      setData(res.data.data);
    } catch (err) {
      showMessage(setErrorMessage, err.message || "Failed to fetch data");
    }
  };

  const getDocbyTransleterID = async (id, shouldToggle = true) => {
    setRequestID(id);
    try {
      const res = await axios.get(
        `${API_BASE}/translate/docs?requestId=${id}`,
        { headers: getAuthHeaders() }
      );

      const translateDocs = res.data.data.map((doc) => ({
        id: doc.id,
        document: doc.documentUrl,
        documentId: doc.documentId,
        templateId: doc.templateId,
        templateUrl: doc.templateUrl,
        translatedDocumentId: doc.translatedDocumentId,
        translatedDocumentUrl: doc.translatedDocumentUrl,
        translation: "—",
        delivery: "—",
        creationDate: doc.creationDate,
      }));

      // Update the documents data
      setOpenRequests((prev) => ({
        ...prev,
        [id]: translateDocs,
      }));

      // Only toggle open/close if shouldToggle is true
      if (shouldToggle) {
        setCurrentOpenRequestId((prevId) => 
          prevId === id ? null : id
        );
      }
    } catch (err) {
      showMessage(setErrorMessage, "Failed to fetch documents");
    }
  };

  const handleStatusChange = async () => {
    try {
      const res = await axios.patch(
        `${API_BASE}/translate/requests/${requestID}/status?status=PROOFREADING`,
        {},
        { headers: getAuthHeaders() }
      );

      showMessage(setSuccessfulMessage, 'Status changed successfully to Proofreading');
      await getTranslateData();
      setShowPopup(false);

    } catch (error) {
      const errorMsg = error.response?.data?.data || "Failed to update status";
      showMessage(setErrorMessage, errorMsg);
      setShowPopup(false);
    }
  };

  const handleFileChange = async (event, rowId) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingRows((prev) => ({ ...prev, [rowId]: true }));
      setSelectedFiles((prev) => ({ ...prev, [rowId]: file }));

      const formData = new FormData();
      formData.append("file", file);

      await axios.patch(
        `${API_BASE}/translate/docs/${rowId}/translated-document`,
        formData,
        { headers: getAuthHeaders() }
      );

      showMessage(setUploadMessage, 'Document uploaded successfully');
      // Pass false to update without closing
      await getDocbyTransleterID(currentOpenRequestId, false);
    } catch (err) {
      const errorMsg = err?.response?.data?.data || err.message;
      showMessage(setErrorMessage, errorMsg);
    } finally {
      setUploadingRows((prev) => ({ ...prev, [rowId]: false }));
      // Clear the file input so the same file can be uploaded again
      if (fileInputRefs.current[rowId]) {
        fileInputRefs.current[rowId].value = '';
      }
    }
  };

  const handleDownload = async (mediaId, name) => {
    try {

      let splitFileName = name.split('.com/')[1];
      console.log('splitFileName', splitFileName);

      const url = `${API_BASE}/media/download/${mediaId}`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });

      const blob = response.data;
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;

      const contentDisposition = response.headers["content-disposition"];
      let fileName = splitFileName;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match?.[1]) {
          fileName = match[1].replace(/['"]/g, "");
        }
      }

      if (!fileName.includes(".")) {
        const contentType = response.headers["content-type"];
        const ext = contentType?.split("/")[1] || "bin";
        fileName = `${fileName}.${ext}`;
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      showMessage(setErrorMessage, "Download failed");
    }
  };

  const handleIconClick = (rowId) => {
    fileInputRefs.current[rowId]?.click();
  };

  const handleNextClick = () => {
    setShowPopup(true);
  };

  const handleCancel = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    getTranslateData();
  }, []);

  const renderMessage = (message, type = 'success') => {
    if (!message) return null;

    return (
      <div
        className="popup-message"
        style={type === 'error' ? { backgroundColor: 'red' } : {}}
      >
        {message}
      </div>
    );
  };

  const renderTaskField = (label, value) => (
    <div style={{ flex: '1 1 50px', minWidth: '50px' }}>
      <strong style={{ fontSize: '14px', fontWeight: '500' }}>{label}</strong>
      <br />
      {value || "N/A"}
    </div>
  );

  const renderConfirmationPopup = () => {
    if (!showPopup) return null;

    return (
      <div style={styles.popupOverlay}>
        <div style={styles.popupContent}>
          <h3 style={styles.popupTitle}>
            Are you sure you want to change status to Proofreading?
          </h3>
          <div style={styles.popupButtons}>
            <button onClick={handleCancel} style={styles.cancelButton}>
              Cancel
            </button>
            <button onClick={handleStatusChange} style={styles.confirmButton}>
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleDeleteClick = (rowId) => {
    setSelectedRowId(rowId);
    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedRowId(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(
        `${API_BASE}/translate/docs/${selectedRowId}/translated-document`,
        { headers: getAuthHeaders() }
      );
      setSuccessfulDelete('successful delete This Doc')
      setTimeout(() => {
        setSuccessfulDelete('');
      }, 3000);

      // Pass false to update without closing
      getDocbyTransleterID(currentOpenRequestId, false)
      setDeleteDialogOpen(false);
      setSelectedRowId(null);
      
      // Clear the file input after delete
      if (fileInputRefs.current[selectedRowId]) {
        fileInputRefs.current[selectedRowId].value = '';
      }
    } catch (err) {
      console.log('error when delete this doc', err.response.data.data);
    }

  };

  const renderTranslationCell = (row) => {
    return (

      <td className="styled-cell">
        <input
          type="file"
          ref={(el) => (fileInputRefs.current[row.id] = el)}
          style={{ display: "none" }}
          onChange={(e) => handleFileChange(e, row.id)}
          accept="*/*"
        />

        {uploadingRows[row.id] ? (
          <CircularProgress size={16} style={{ color: "#1976d2" }} />
        ) : row.translatedDocumentUrl ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <a
              onClick={() => handleDownload(row.translatedDocumentId, row.translatedDocumentUrl)}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.link}
            >
              {row?.translatedDocumentUrl.split(".com/")[1]?.length > 25
                ? row?.translatedDocumentUrl.split(".com/")[1].slice(0, 25) + "..."
                : row?.translatedDocumentUrl.split(".com/")[1]}
            </a>

            <DeleteIcon
              onClick={() => handleDeleteClick(row.id)}
              style={{ fontSize: "18px", color: "red", cursor: "pointer" }}
            />
          </div>
        ) : (
          <FileUploadIcon
            style={{ fontSize: "16px", cursor: "pointer" }}
            onClick={() => handleIconClick(row.id)}
          />
        )}
      </td>

    );
  };

  const renderDocumentTable = (taskId) => {
    const docs = openRequests[taskId];
    if (!docs) return null;

    return (
      <>
        <table className="styled-table" style={{ marginTop: "10px", width: "100%" }}>
          <thead className="styled-thead">
            <tr>
              <th style={styles.tableHeader}>Document (PDF)</th>
              <th style={styles.tableHeader}>Template (Word)</th>
              <th style={styles.tableHeader}>Translation (Word)</th>
              <th style={styles.tableHeader}>Created At</th>
            </tr>
          </thead>
          <tbody className="styled-tbody">
            {docs.map((row, idx) => (
              <tr className="styled-row" key={idx}>
                <td className="styled-cell">
                  <a
                    onClick={() => handleDownload(row.documentId, row.document)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.link}
                  >
                    {row?.document.split('.com/')[1]};
                  </a>
                </td>

                <td className="styled-cell">
                  {row.templateUrl ? (
                    <a
                      onClick={() => handleDownload(row.templateId, row.templateUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.link}
                    >
                      {row?.templateUrl.split('.com/')[1]};
                    </a>
                  ) : (
                    "No template uploaded"
                  )}
                </td>

                {renderTranslationCell(row)}

                <td className="styled-cell">
                  {row?.creationDate?.substring(0, 10) || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "end" }}>
          <button onClick={handleNextClick} style={styles.nextButton}>
            NEXT
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="Translator-container" style={{ padding: '50px 100px' }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontWeight: '400' }}>Translator Management</h2>
      </div>

      <div style={{ padding: '20px' }}>
        {data.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No translation requests found.
          </div>
        ) : (
          data.map((taskDetails, index) => (
            <div key={taskDetails.id || index} style={{ marginBottom: "20px" }}>
              <div
                onClick={() => getDocbyTransleterID(taskDetails.id, true)}
                className="task-row"
                style={styles.taskRow}
              >
                {renderTaskField("Client", taskDetails.clientName)}
                {renderTaskField("Request Date", formatDate(taskDetails.requestDate))}
                {renderTaskField("Task Title", taskDetails.taskTitle || `Task #${taskDetails.id}`)}
                {renderTaskField("Total Pages", taskDetails.totalPages)}
                {renderTaskField("Translator", taskDetails.translatorName)}
                {renderTaskField("Proofreader", taskDetails.proofreaderName)}
                {renderTaskField("Delivery Date", formatDate(taskDetails.deliveryDate))}

                <div style={{ flex: '1 1 50px', minWidth: '50px' }}>
                  <strong style={{ fontSize: '14px', fontWeight: '500' }}>Status</strong>
                  <br />
                  <span className="status" style={styles.statusBadge}>
                    {taskDetails.status || "Unknown"}
                  </span>
                </div>
              </div>

              {currentOpenRequestId === taskDetails.id && renderDocumentTable(taskDetails.id)}
            </div>
          ))
        )}
      </div>

      {deleteDialogOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              Confirm Delete
            </h3>
            <p style={{ margin: '0 0 30px 0', color: '#666' }}>
              Are you sure you want to delete this document?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancelDelete}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: 'red',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {renderConfirmationPopup()}
      {renderMessage(errorMessage, 'error')}
      {renderMessage(successfulMessage)}
      {renderMessage(uploadMessage)}
      {renderMessage(successfulDelete)}
    </div>
  );
};

const styles = {
  taskRow: {
    cursor: "pointer",
    margin: "30px 0px",
    padding: "10px 30px",
    borderRadius: "12px",
    display: 'flex',
    flexWrap: 'wrap',
    border: "1px solid #000",
  },
  tableHeader: {
    fontWeight: '500',
    color: "#000",
    fontSize: '14px'
  },
  link: {
    color: "blue",
    textDecoration: "underline",
    cursor: "pointer"
  },
  nextButton: {
    marginTop: '10px',
    fontSize: '14px',
    backgroundColor: "transparent",
    padding: "5px 25px",
    border: "1px solid #000",
    cursor: "pointer",
    borderRadius: "3px",
  },
  statusBadge: {
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    color: "#000",
    fontWeight: '500',
    backgroundColor: "#55D55B",
  },
  popupOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999
  },
  popupContent: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    maxWidth: '400px',
    width: '90%'
  },
  popupTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    textAlign: 'center',
    color: '#333'
  },
  popupButtons: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center'
  },
  cancelButton: {
    padding: '8px 20px',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  confirmButton: {
    padding: '8px 20px',
    backgroundColor: '#3F302E',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  }
};

export default Translator;